import { computed } from 'vue'

import {
  useStudioPlusCampaign,
  useStudioPlusPrice,
  usePreviewPrice,
  useZuoraAccount,
} from '@/components/payment/queries'
import { countries } from '@/constants/countries'
import { STUDIO_PLUS_BILLING_TERM, STUDIO_PLUS_PRODUCT_TYPE } from '@/constants/premiumProducts'
import { useSessionValues } from '@/composables/useSessionValues'

/**
 * Encapsulates all pricing-related data fetching and derivations for the
 * Pro-to-Plus reactivation flow.
 *
 * @param {import('vue').Ref} accommodationId - reactive accommodation ID ref
 */
export function useProToPlusPricingData(accommodationId) {
  const { currency } = useSessionValues()

  const { studioPlusCampaign, isPlusDataInitialLoading } = useStudioPlusCampaign(accommodationId)

  // Preview price returns the per-billing-cycle amount from Zuora (display units).
  const { amountWithoutTax: proPreviewAmountWithoutTax, isLoading: isPreviewPriceLoading } = usePreviewPrice({
    selectedAccommodationId: accommodationId,
    productType: STUDIO_PLUS_PRODUCT_TYPE.PRO,
    enabled: computed(() => studioPlusCampaign.value?.type === STUDIO_PLUS_PRODUCT_TYPE.PRO),
  })

  // Normalize PRO price to monthly: MONTHLY term uses amount directly, YEARLY divides by 12.
  const proMonthlyPrice = computed(() => {
    const amount = proPreviewAmountWithoutTax.value ?? 0
    const term = studioPlusCampaign.value?.term
    if (term === STUDIO_PLUS_BILLING_TERM.YEARLY) return amount / 12
    return amount
  })

  const { zuoraAccountData, isLoading: isZuoraDataLoading } = useZuoraAccount()
  const countryCode = computed(
    () => countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code
  )

  const { studioPlusPrice, isLoading: isPlusPriceLoading } = useStudioPlusPrice({
    selectedAccommodationId: accommodationId,
    currency,
    countryCode,
    postalCode: computed(() => zuoraAccountData.value?.billToContact?.zipCode),
    countryState: computed(() => zuoraAccountData.value?.billToContact?.state),
    taxId: computed(() => zuoraAccountData.value?.taxInfo?.exemptCertificateId),
    enabled: computed(() => !isZuoraDataLoading.value && !!zuoraAccountData.value),
  })

  // baseSubscriptionMonthlyPrice and baseSubscriptionFullTermPrice are net
  // (without tax) amounts in cents – formatCurrency divides by 100 by default.
  const plusMonthlyPrice = computed(() => studioPlusPrice.value?.baseSubscriptionMonthlyPrice ?? 0)
  const annualPrice = computed(() => studioPlusPrice.value?.baseSubscriptionFullTermPrice ?? 0)

  const isPriceLoading = computed(
    () =>
      isPlusDataInitialLoading.value ||
      isZuoraDataLoading.value ||
      isPlusPriceLoading.value ||
      isPreviewPriceLoading.value
  )

  // Compare in display units: proMonthlyPrice (already display) vs Plus monthly (cents → display)
  const plusMonthlyDisplay = computed(() => plusMonthlyPrice.value / 100)
  const isPricingComparison = computed(
    () => proMonthlyPrice.value > 0 && plusMonthlyDisplay.value > 0 && proMonthlyPrice.value > plusMonthlyDisplay.value
  )
  const monthlySavings = computed(() =>
    isPricingComparison.value ? proMonthlyPrice.value - plusMonthlyDisplay.value : 0
  )

  return {
    isPriceLoading,
    plusMonthlyPrice,
    annualPrice,
    proPrice: proMonthlyPrice,
    isPricingComparison,
    monthlySavings,
  }
}
